/**
 * The network-health cockpit's contract — owner rulings DEC-107, DEC-108, DEC-109, DEC-110,
 * bata-platform#504 / PR #505, design brief bata-ui#268 (the designer's brief part 4). `docs/adr/9046`.
 *
 * ══ PINNED ═══════════════════════════════════════════════════════════════════
 *
 * `src/api/spec/openapi.json` is bata-platform v0.61.0 (v0.60.0 published the reads; v0.61.0 names articles), which publishes
 * `GET /api/v1/inseason/runs/{run_id}/network-health` and `…/network-health/sizes` (#505, ADR 6055,
 * merged). So the shapes below are the generated ones, re-exported under the names the pages and
 * tests already import, and {@link NETWORK_HEALTH_READ} / {@link NETWORK_HEALTH_SIZES_READ} are the
 * generated operations. Until this re-pin the file held #505's shapes typed by hand and both
 * constants were `null` (every page drew «In arrivo»); the generated shapes matched them field for
 * field. Level 3 reads each size's `healthState` / `healthReason` / `isUncovered` from the sizes
 * read, so level 0 and level 3 can never disagree. `network-health.test.tsx` keeps the tripwire:
 * the pinned spec has each path exactly when its constant is wired.
 *
 * **Every field access is in this file.** The pages read {@link HealthCounts}, which
 * {@link healthCounts} builds, never a member of the platform's shapes.
 *
 * **DEC-108** — `cellsByState` (`ok`, `low`, `out`, `broken`, `na`) partitions the served cells by
 * na › broken › out › low › ok and sums to `cellsServed`; `out + broken = cellsOos`;
 * `cellsUncoveredByState` (`low`, `out`, `broken`); `cellsNaByReason`. **DEC-109 as the platform
 * verified it** — `inseason.rebalance` writes no transfer lines; the distribute run writes them
 * under its own id, so a run's proposals AND transfers are its own, and only the price steps come
 * from another run of the tick: `interventionRuns.rebalance` is `null` with
 * `rebalanceAbsentReason: stage_records_no_lines`. **DEC-110** — the sweep observes only the
 * market's newest live run and only active rules: on any other run cover and size gap are
 * `not_observed_for_run`, which the cockpit says as unknown, never 0.
 *
 * ══ PINNED: THE MOVE RUN (bata-platform ADR 6055, amended 2026-10-31; v0.126.5, #909) ════════
 *
 * Since bata-platform ADR 9049 (2026-10-31) every store-to-store move is a line of
 * `compacting.move_run`, and the distribute run writes no transfer row. The platform fix (bata-reqs
 * docs/25 §4.1) adds to the same two reads, and the pinned spec (bata-platform v0.126.5) declares them,
 * so the shapes below are the generated ones (`docs/adr/9037`'s pattern, `docs/adr/9046` amended):
 *
 *   interventionRuns.move / .moveAbsentReason   the tick's move run, or why none
 *                                               (`run_reads_transfers` on a run recorded before the
 *                                               retirement, `no_move_run_in_tick`, `move_run_failed`,
 *                                               `run_has_no_cycle_tick`)
 *   moveLinesUnmatchedByReason                  inbound move lines that name no cell of the run
 *   sizes: moveRun / moveAbsentReason / moves   the counted lines in and out of the store × article
 *
 * The fields are optional in the spec, so they are still read only when the serving platform sends
 * them, and a platform that does not (a release before v0.126.5) is said as `move_not_published` —
 * never as «no move». {@link NETWORK_HEALTH_CARRIES_MOVES} is `true`; the tripwire in
 * `network-health.test.tsx` holds it to the pinned `NetworkHealthInterventionRuns`.
 */

import { networkHealth, networkHealthSizes } from '../generated/operations';
import type {
  NetworkHealthCriterion as GeneratedCriterion, NetworkHealthDimension as GeneratedDimension,
  NetworkHealthGroup, NetworkHealthInterventionRuns, NetworkHealthMove, NetworkHealthPage, NetworkHealthSize,
  NetworkHealthSizes, NetworkHealthTotals,
} from '../generated/types';

type Equals<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

// ── the vocabularies ─────────────────────────────────────────────────────────

/** What the run's cells are grouped by (ADR 6055 §1). */
export const NETWORK_HEALTH_DIMENSIONS = ['country', 'department_family', 'store', 'article'] as const;
export type NetworkHealthDimension = (typeof NETWORK_HEALTH_DIMENSIONS)[number];

/** The five states, in the bar's fixed order (brief part 4 §1.4). */
export const HEALTH_STATES = ['ok', 'low', 'out', 'broken', 'na'] as const;
export type HealthState = (typeof HEALTH_STATES)[number];

/** The three states that are problems — the lens, the «Scoperti» column (§1.4, §3). */
export const PROBLEM_STATES = ['low', 'out', 'broken'] as const;
export type ProblemState = (typeof PROBLEM_STATES)[number];

/** The sweep's absence for a run it did not observe (DEC-110, ADR 6055 §3). */
export const NOT_OBSERVED_FOR_RUN = 'not_observed_for_run';

/** The department-family filter's no-family bucket (ADR 2109). */
export const NO_FAMILY = '-';

/**
 * Whether the pinned `NetworkHealthInterventionRuns` declares `move` (bata-platform ADR 6055, amended
 * 2026-10-31; pinned since v0.126.5). The tripwire in `network-health.test.tsx` holds it to the spec.
 */
export const NETWORK_HEALTH_CARRIES_MOVES = true;

/** Why the run's moves are not a move run's lines — the platform's codes, and this client's own. */
export const RUN_READS_TRANSFERS = 'run_reads_transfers';
export const NO_MOVE_RUN_IN_TICK = 'no_move_run_in_tick';
export const MOVE_RUN_FAILED = 'move_run_failed';
/** The serving platform does not publish the move run (a release before the fix): unknown, never «none». */
export const MOVE_NOT_PUBLISHED = 'move_not_published';
/** A move line naming a size the destination's cells do not hold (platform #907's spelling). */
export const MOVE_SIZE_UNTRANSLATED = 'move_size_untranslated';

/** One counted line of the tick's move run at a store × article, as the sizes read lists it (generated). */
export type NetworkHealthMoveShape = NetworkHealthMove;

// ── the shapes, generated (bata-platform v0.60.0) ────────────────────────────

/**
 * The re-pin: each name the pages and tests import is now the generated shape. The hand-typed
 * shapes of #505 (d15777c) matched the generated ones field for field; the counts are one shape
 * the generator splits into `NetworkHealthGroup` and `NetworkHealthTotals`.
 */
export type NetworkHealthCriterion = GeneratedCriterion;
export type NetworkHealthCountsShape = Omit<NetworkHealthTotals, "groups">;
export type NetworkHealthGroupShape = NetworkHealthGroup;
export type NetworkHealthTotalsShape = NetworkHealthTotals;
export type InterventionRunsShape = NetworkHealthInterventionRuns;
export type NetworkHealthPageShape = NetworkHealthPage;
export type NetworkHealthSizeShape = NetworkHealthSize;
export type NetworkHealthSizesShape = NetworkHealthSizes;

/** The dimension vocabulary is held equal to the generated enum. */
const DIMENSIONS_MATCH: Equals<NetworkHealthDimension, GeneratedDimension> = true;
export const NETWORK_HEALTH_VOCABULARIES_MATCH = DIMENSIONS_MATCH;

export interface NetworkHealthSizesParams {
  readonly path: { readonly run_id: string };
  readonly query: { readonly store_key: string; readonly article_key: string };
}

export type NetworkHealthSizesRead = typeof networkHealthSizes;

/** The generated operation. Typed `| null` still, so a test can mount the not-pinned state. */
export const NETWORK_HEALTH_SIZES_READ: NetworkHealthSizesRead | null = networkHealthSizes;

export const NETWORK_HEALTH_SIZES_PATH_IN_SPEC = '/api/v1/inseason/runs/{run_id}/network-health/sizes';

export interface NetworkHealthParams {
  readonly path: { readonly run_id: string };
  readonly query: {
    readonly group_by: NetworkHealthDimension;
    readonly store_key?: string;
    readonly article_key?: string;
    readonly department_family?: string;
    /**
     * Only the groups with at least one served cell in one of these states, REPEATED on the wire
     * (`state=out&state=broken`): bata-platform's `state` query parameter (cockpit KPI cards,
     * `docs/adr/9046`). The pinned spec declares it (`networkHealth.queryParams`), so the generated operation sends it as is.
     */
    readonly state?: readonly ProblemState[];
    readonly cursor?: string;
    readonly limit?: number;
  };
}

export type NetworkHealthRead = typeof networkHealth;

/**
 * The operation as b6dc06f declares it — `operationId`, path, parameters. **Not wired to any
 * page**: a test mounts a page with it over a stand-in `fetch`, so the page's whole rendering is
 * exercised before the re-pin. Production reads {@link NETWORK_HEALTH_READ}, which is `null`.
 */
export const NETWORK_HEALTH_PROPOSED_OPERATION: NetworkHealthRead = {
  operationId: 'network_health_api_v1_inseason_runs__run_id__network_health_get',
  method: 'GET',
  path: '/api/v1/inseason/runs/{run_id}/network-health',
  tag: 'inseason',
  pathParams: ['run_id'],
  queryParams: ['group_by', 'store_key', 'article_key', 'department_family', 'state', 'cursor', 'limit'],
  hasBody: false,
};

/** The generated operation. Typed `| null` still, so a test can mount «In arrivo» explicitly. */
export const NETWORK_HEALTH_READ: NetworkHealthRead | null = networkHealth;

/** What the re-pin tripwire looks for in the pinned spec. */
export const NETWORK_HEALTH_PATH_IN_SPEC = '/api/v1/inseason/runs/{run_id}/network-health';

/** 404: no network-health source is installed in the serving process — «In arrivo», never zeroes. */
export const NETWORK_HEALTH_UNAVAILABLE = 'planning.inseason.network_health_unavailable';

/** Groups asked per page on level 1 — the first fifty in the platform's order (ADR 9034). */
export const NETWORK_HEALTH_GROUP_LIMIT = 50;

// ── the page's reading of a page ─────────────────────────────────────────────

/** One state's cells, and how many of them no intervention covers (`null`: not a problem state). */
export interface StateCount {
  readonly cells: number;
  readonly uncovered: number | null;
}

/** Why a figure cannot be stated — a catalogue key's last segment (`inseason.networkHealth.absent.*`). */
export type HealthAbsence =
  | 'partitionNotPublished'
  | 'criterionAbsent'
  | 'notMeasured';

/**
 * A group's counts as the cockpit reads them. Built ONLY by {@link healthCounts}: the pages read
 * this, never the platform's members.
 */
export interface HealthCounts {
  readonly key: string | null;
  readonly label: string | null;
  readonly served: number;
  readonly notRanged: number;
  /** The partition (sums to `served`), or why it cannot be drawn. */
  readonly partition:
    | { readonly kind: 'drawn'; readonly states: Readonly<Record<HealthState, StateCount>> }
    | { readonly kind: 'absent'; readonly reason: HealthAbsence };
  /** Stock-out per size and per article (DEC-107). Per size includes the sizes in a broken run. */
  readonly oosCells: number;
  readonly oosCellsUncovered: number;
  readonly oosStoreArticles: number;
  /** Store × articles below the cover rule; `null` when the rule cannot be applied. */
  readonly lowCoverStoreArticles: number | null;
  readonly lowCoverStoreArticlesUncovered: number | null;
  readonly coverMeasuredStoreArticles: number;
  readonly storeArticles: number;
  /** Cells in a broken size run; `null` when the size-run rule cannot be applied. */
  readonly brokenCells: number | null;
  readonly brokenCellsUncovered: number | null;
  /** Every uncovered problem, at cell grain: the partition's — `null` without it. */
  readonly uncovered: number | null;
  /**
   * Uncovered cells with an open alert on their store × article («avviso aperto»), and with a price
   * step of the tick's price run on their article («intervento di prezzo») — BESIDE the uncovered,
   * never cover (owner rulings F1/F2, bata-platform#559). The second is `null` when the tick has no
   * price run.
   */
  readonly uncoveredWithOpenAlert: number;
  readonly uncoveredWithPriceStep: number | null;
  readonly interventions: {
    readonly proposalCells: number;
    readonly proposalUnits: number;
    readonly transferInCells: number;
    readonly transferInUnits: number;
    readonly transferOutCells: number;
    readonly transferOutUnits: number;
    readonly openAlertStoreArticles: number;
    readonly openAlertsByRule: Readonly<Record<string, number>>;
    readonly priceStepArticles: number;
  };
  readonly freshShare: number | null;
  /** DEC-110: the sweep did not observe this run's cover / size gaps — every such figure is unknown. */
  readonly coverNotObserved: boolean;
  readonly sizeGapNotObserved: boolean;
  /** Why the `na` cells are not evaluable. */
  readonly naByReason: Readonly<Record<string, number>>;
  /**
   * With a `state` filter on the read: the group's served cells in those states, and how many of
   * them nothing covers (`stateCells` / `stateUncovered`). `null` without the filter — or when the
   * platform ignored it, which the page says rather than pretend (older platform).
   */
  readonly stateCells: number | null;
  readonly stateUncovered: number | null;
}

/** What a group carries beside the counts when the read was filtered by state. */
export interface StateCountsShape {
  readonly stateCells?: number | null;
  readonly stateUncovered?: number | null;
}

/** The cockpit's reading of one group (or of the totals). Pure. */
export function healthCounts(
  counts: NetworkHealthCountsShape & StateCountsShape,
  key: string | null = null,
  label: string | null = null,
): HealthCounts {
  const byState = counts.cellsByState;
  const uncovered = counts.cellsUncoveredByState;
  const num = (record: Readonly<Record<string, number>> | undefined, key: string): number | null => {
    const value = record?.[key];
    return typeof value === 'number' ? value : null;
  };
  const cells = HEALTH_STATES.map((state) => num(byState, state));
  const open = PROBLEM_STATES.map((state) => num(uncovered, state));
  // A partition with a state missing is not drawn: a bar over four states would read as five.
  const partition: HealthCounts['partition'] = cells.some((value) => value === null) || open.some((value) => value === null)
    ? { kind: 'absent', reason: 'partitionNotPublished' }
    : {
      kind: 'drawn',
      states: {
        ok: { cells: num(byState, 'ok') ?? 0, uncovered: null },
        low: { cells: num(byState, 'low') ?? 0, uncovered: num(uncovered, 'low') },
        out: { cells: num(byState, 'out') ?? 0, uncovered: num(uncovered, 'out') },
        broken: { cells: num(byState, 'broken') ?? 0, uncovered: num(uncovered, 'broken') },
        na: { cells: num(byState, 'na') ?? 0, uncovered: null },
      },
    };
  // DEC-110: nothing measured and the sweep did not observe this run — unknown, never 0.
  const notObserved = (measured: number, absent: Readonly<Record<string, number>>): boolean => (
    measured === 0 && (absent[NOT_OBSERVED_FOR_RUN] ?? 0) > 0
  );
  return {
    key,
    label,
    served: counts.cellsServed,
    notRanged: counts.cellsNotRanged,
    partition,
    oosCells: counts.cellsOos,
    oosCellsUncovered: counts.cellsOosWithoutIntervention,
    oosStoreArticles: counts.storeArticlesOos,
    lowCoverStoreArticles: counts.storeArticlesBelowCover,
    lowCoverStoreArticlesUncovered: counts.storeArticlesBelowCoverWithoutIntervention,
    coverMeasuredStoreArticles: counts.storeArticlesCoverMeasured,
    storeArticles: counts.storeArticlesTotal,
    brokenCells: counts.cellsSizeGap,
    brokenCellsUncovered: counts.cellsSizeGapWithoutIntervention,
    uncovered: partition.kind === 'drawn' ? open.reduce<number>((sum, value) => sum + (value ?? 0), 0) : null,
    uncoveredWithOpenAlert: counts.cellsUncoveredWithOpenAlert,
    uncoveredWithPriceStep: counts.cellsUncoveredWithPriceStep,
    interventions: {
      proposalCells: counts.cellsWithProposal,
      proposalUnits: counts.proposalUnits,
      transferInCells: counts.cellsWithTransferIn,
      transferInUnits: counts.transferInUnits,
      transferOutCells: counts.cellsWithTransferOut,
      transferOutUnits: counts.transferOutUnits,
      openAlertStoreArticles: counts.storeArticlesWithOpenAlert,
      openAlertsByRule: counts.storeArticlesWithOpenAlertByRule,
      priceStepArticles: counts.articlesWithPriceStep,
    },
    freshShare: counts.partitionIsFreshShare,
    coverNotObserved: notObserved(counts.storeArticlesCoverMeasured, counts.coverAbsentByReason),
    sizeGapNotObserved: notObserved(counts.cellsSizeGapMeasured, counts.sizeGapAbsentByReason),
    naByReason: counts.cellsNaByReason,
    stateCells: typeof counts.stateCells === 'number' ? counts.stateCells : null,
    stateUncovered: typeof counts.stateUncovered === 'number' ? counts.stateUncovered : null,
  };
}

/** A page as the cockpit reads it: its groups, its totals and the run's facts. Pure. */
export interface HealthPage {
  readonly runId: string;
  readonly market: string;
  readonly asOf: string | null;
  readonly groups: readonly HealthCounts[];
  readonly groupCount: number;
  readonly totals: HealthCounts;
  /** The cover rule's threshold in force for the run's market on its stock date, `null` when absent. */
  readonly coverThreshold: number | null;
  /** The cover rule's absence reason, `null` when it applies. */
  readonly coverAbsent: string | null;
  readonly sizeGapAbsent: string | null;
  /** The market's class scheme is not pinned or refused: every article in no family (ADR 6039). */
  readonly familyAbsent: string | null;
  readonly nextCursor: string | null;
  /** The runs the interventions were read from (DEC-109). */
  readonly interventionRuns: InterventionRuns;
  /** The inbound moves that name no cell of the run, by reason ({@link movesUnmatchedOf}). */
  readonly movesUnmatched: Readonly<Record<string, number>>;
}

/**
 * DEC-109 as the platform verified it: a run's proposals and transfers are its own (the rebalance
 * stage writes no lines); only the price steps come from another run of the tick, or say why none.
 */
export interface InterventionRuns {
  readonly proposals: string;
  /** The run whose transfer rows are read — a run recorded before the move run (history). */
  readonly transfers: string;
  readonly price: string | null;
  readonly priceAbsentReason: string | null;
  /**
   * The tick's move run, whose lines are the run's store-to-store moves (bata-platform ADR 6055,
   * amended 2026-10-31); `null` with {@link movesAbsentReason} — {@link RUN_READS_TRANSFERS} when the
   * run's moves are its own transfer rows (history: read {@link transfers}, never both).
   */
  readonly moves: string | null;
  readonly movesAbsentReason: string | null;
  /** When the move run ran, for its name in words; `null` when only its id is known. */
  readonly movesRanOn: string | null;
}

/** Whether the run's moves are its own transfer rows (a run recorded before the move run). Pure. */
export function readsTransfers(runs: InterventionRuns): boolean {
  return runs.moves === null && runs.movesAbsentReason === RUN_READS_TRANSFERS;
}

export function interventionRunsOf(runs: InterventionRunsShape): InterventionRuns {
  const published = Object.hasOwn(runs, 'move') || Object.hasOwn(runs, 'moveAbsentReason');
  return {
    proposals: runs.distribute,
    transfers: runs.rebalance ?? runs.distribute,
    price: runs.price,
    priceAbsentReason: runs.priceAbsentReason,
    moves: published ? runs.move ?? null : null,
    movesAbsentReason: published ? (runs.move ?? null) === null ? runs.moveAbsentReason ?? null : null : MOVE_NOT_PUBLISHED,
    movesRanOn: null,
  };
}

/**
 * The inbound move lines of the tick's move run that name no cell of the run, by reason — counted by the
 * platform, never dropped (`move_size_untranslated`, `not_in_partition`). Empty when not published. Pure.
 */
export function movesUnmatchedOf(page: NetworkHealthPageShape): Readonly<Record<string, number>> {
  return page.moveLinesUnmatchedByReason ?? {};
}

/**
 * The totals of a read filtered by state: the cells in those states and the uncovered among them,
 * summed from the partition of the groups listed (`totals` count the groups listed), so the total
 * row of the filtered list IS the card that opened it (ADR 9069). Nothing unless the platform
 * applied the filter (its groups carry `stateCells`).
 */
function stateTotals(page: NetworkHealthPageShape, states: readonly ProblemState[]): StateCountsShape {
  const applied = states.length > 0 && page.items.some((item) => typeof (item as StateCountsShape).stateCells === 'number');
  if (!applied) return {};
  const sum = (record: Readonly<Record<string, number>> | undefined): number => states.reduce((total, state) => total + (record?.[state] ?? 0), 0);
  return { stateCells: sum(page.totals.cellsByState), stateUncovered: sum(page.totals.cellsUncoveredByState) };
}

/** `states`: the filter the read was asked with, for the total row. */
export function healthPage(page: NetworkHealthPageShape, states: readonly ProblemState[] = []): HealthPage {
  return {
    runId: page.runId,
    market: page.countryKey,
    asOf: page.asOf,
    groups: page.items.map((item) => healthCounts(item, item.dimensionKey, item.dimensionLabel)),
    groupCount: page.totals.groups,
    totals: healthCounts({ ...page.totals, ...stateTotals(page, states) }),
    coverThreshold: page.cover.threshold,
    coverAbsent: page.cover.absentReason,
    sizeGapAbsent: page.sizeGap.absentReason,
    familyAbsent: page.departmentFamilyAbsentReason,
    nextCursor: page.nextCursor,
    interventionRuns: interventionRunsOf(page.interventionRuns),
    movesUnmatched: movesUnmatchedOf(page),
  };
}

/**
 * The name the platform gives a group's key — a store's on the door master (`group_by=store`),
 * an article's on the attribute row in force (`group_by=article`, bata-platform v0.61) — or
 * `null` when it publishes none. The drill headers read it in place of «Nome in arrivo».
 */
export function dimensionLabelOf(page: NetworkHealthPageShape | undefined, key: string | null): string | null {
  if (page === undefined || key === null) return null;
  return page.items.find((group) => group.dimensionKey === key)?.dimensionLabel ?? null;
}

/** The read's parameters for one level. Pure. */
export function networkHealthParams(runId: string, groupBy: NetworkHealthDimension, filters: {
  readonly storeKey?: string | null;
  readonly articleKey?: string | null;
  readonly departmentFamily?: string | null;
  /** Only the groups with a served cell in one of these states (empty: all of them). */
  readonly states?: readonly ProblemState[];
  readonly limit?: number;
} = {}): NetworkHealthParams {
  return {
    path: { run_id: runId },
    query: {
      group_by: groupBy,
      ...(filters.storeKey === undefined || filters.storeKey === null ? {} : { store_key: filters.storeKey }),
      ...(filters.articleKey === undefined || filters.articleKey === null ? {} : { article_key: filters.articleKey }),
      ...(filters.departmentFamily === undefined || filters.departmentFamily === null ? {} : { department_family: filters.departmentFamily }),
      ...(filters.states === undefined || filters.states.length === 0 ? {} : { state: filters.states }),
      limit: filters.limit ?? NETWORK_HEALTH_GROUP_LIMIT,
    },
  };
}

// ── the level 3 read (sizes) ─────────────────────────────────────────────────

/**
 * The sizes operation as #505 declares it. **Not wired**: a test runs level 3 over it with a
 * stand-in `fetch`, so the re-pin code path is exercised today. Production reads
 * {@link NETWORK_HEALTH_SIZES_READ}, which is `null`.
 */
export const NETWORK_HEALTH_SIZES_PROPOSED_OPERATION: NetworkHealthSizesRead = {
  operationId: 'network_health_sizes_api_v1_inseason_runs__run_id__network_health_sizes_get',
  method: 'GET',
  path: NETWORK_HEALTH_SIZES_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: ['run_id'],
  queryParams: ['store_key', 'article_key'],
  hasBody: false,
};

/** One size's state as the platform states it — the platform's reason CODE, mapped by the page. */
export interface SizeHealth {
  readonly served: boolean;
  readonly state: HealthState | null;
  readonly reasonCode: string | null;
  readonly uncovered: boolean | null;
  /** bata-ui#302 E (ADR 6057): why the run wrote no line for the cell, and the target and need behind it. */
  readonly uncoveredReason: string | null;
  readonly targetUnits: number | null;
  readonly needUnits: number | null;
}

const isHealthState = (value: string | null): value is HealthState => (HEALTH_STATES as readonly string[]).includes(value ?? '');

/** Each size's state by size key, from the sizes read (DEC-108, the same statement as the totals). Pure. */
export function sizeHealthOf(page: NetworkHealthSizesShape): ReadonlyMap<string, SizeHealth> {
  return new Map(page.sizes.map((size) => {
    const known = isHealthState(size.healthState);
    return [size.sizeKey, {
      served: size.isServed,
      // A served size in a state this build has no word for is not evaluable, with the platform's code.
      state: !size.isServed ? null : known ? size.healthState as HealthState : 'na',
      reasonCode: !size.isServed ? 'not_in_partition' : known ? size.healthReason : size.healthState,
      uncovered: size.isUncovered,
      uncoveredReason: size.uncoveredReason ?? null,
      targetUnits: size.targetUnits ?? null,
      needUnits: size.needUnits ?? null,
    }];
  }));
}

/**
 * What the sizes read publishes BESIDE an uncovered size, at store × article level (F1/F2,
 * bata-platform#559): the open alert rules, whether the tick's price run stepped the article — or
 * `null` with why the tick has no price run. Never coverage. Pure.
 */
export interface SizesBeside {
  readonly openAlertRules: readonly string[];
  readonly hasOpenAlert: boolean;
  readonly hasPriceStep: boolean | null;
  readonly priceStepAbsentReason: string | null;
}

/**
 * The move run the sizes read took the store-to-store units from, and its counted lines at the store ×
 * article (`undefined` moveRun: the serving platform does not publish it yet). Pure.
 */
export interface SizesMoves {
  readonly moveRun: string | null | undefined;
  readonly absentReason: string | null;
  readonly legs: readonly NetworkHealthMoveShape[];
}

export function sizesMovesOf(page: NetworkHealthSizesShape): SizesMoves {
  if (!Object.hasOwn(page, 'moveRun') && !Object.hasOwn(page, 'moveAbsentReason')) {
    return { moveRun: undefined, absentReason: MOVE_NOT_PUBLISHED, legs: [] };
  }
  return { moveRun: page.moveRun ?? null, absentReason: page.moveAbsentReason ?? null, legs: page.moves ?? [] };
}

export function sizesBesideOf(page: NetworkHealthSizesShape): SizesBeside {
  return {
    openAlertRules: page.openAlertRules,
    hasOpenAlert: page.hasOpenAlert,
    hasPriceStep: page.hasPriceStep,
    priceStepAbsentReason: page.priceStepAbsentReason,
  };
}

export function networkHealthSizesParams(runId: string, storeKey: string, articleKey: string): NetworkHealthSizesParams {
  return { path: { run_id: runId }, query: { store_key: storeKey, article_key: articleKey } };
}
